import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';
import { CircularProgress, TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import PropTypes from 'prop-types';
import React, { useEffect, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';

import patchReleaseDate from '../../../../../api/oc_product/product/releaseDate/patchReleaseDate';
import parseDateString from '../../../../../utils/parseDateString';

dayjs.extend(customParseFormat);

const reducer = (state, action) => {
  switch (action.type) {
    case 'RELEASE_DATES':
      return {
        ...state,
        releaseDates: action.payload,
      };

    default:
      return state;
  }
};

function ProductInfo({
  parentLoading,
  created,
  lastUpdated,
  releaseDate,
  indexedDate,
}) {
  const siteId = useSelector((state) => state.user.loginResponse?.selectedSite?.SiteId);
  const [loading, setLoading] = useState(true);
  const [releaseDateLoading, setReleaseDateLoading] = useState(false);
  const [hasReleaseDateUpdated, setHasReleaseDateUpdated] = useState(false);
  const [productReleaseDate, setProductReleaseDate] = useState('Released');
  const [productReleaseIsoDate, setProductReleaseIsoDate] = useState();
  const [selectedDateTimeForReleaseDate, setSelectedDateTimeForReleaseDate] = useState('');
  const [updateRequestPending, setUpdateRequestPending] = useState(false);
  const [releasedDate, setReleasedDate] = React.useState(null);

  const initialState = {
    releaseDates: '',
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const { id } = useParams();

  useEffect(() => {
    setLoading(parentLoading);
  }, [parentLoading]);

  useEffect(() => {
    if (releaseDate) {
      setProductReleaseDate(parseDateString(releaseDate));
      setProductReleaseIsoDate(releaseDate);
      setSelectedDateTimeForReleaseDate(new Date(releaseDate));
    }
  }, [releaseDate]);

  const update = () => {
    setReleaseDateLoading(true);
    setUpdateRequestPending(true);

    patchReleaseDate(id, siteId, state.releaseDates)
      .then(() => {
        setUpdateRequestPending(false);
        // Synchronize local state with the saved value
        if (state.releaseDates) {
          setProductReleaseDate(parseDateString(state.releaseDates));
          setProductReleaseIsoDate(state.releaseDates);
          setSelectedDateTimeForReleaseDate(new Date(state.releaseDates));
          toast.success('Release date updated', { autoClose: 2000 });
        } else {
          setProductReleaseDate('Released');
          setProductReleaseIsoDate(null);
          setSelectedDateTimeForReleaseDate(null);
          toast.success('Release date updated to Null', { autoClose: 2000 });
        }
      })
      .catch((error) => {
        console.error(error);
        setUpdateRequestPending(false);
      })
      .finally(() => {
        setHasReleaseDateUpdated(false);
        setUpdateRequestPending(false);
        setReleaseDateLoading(false);
      });
  };

  const cancel = () => {
    setHasReleaseDateUpdated(!hasReleaseDateUpdated);
    setSelectedDateTimeForReleaseDate(productReleaseIsoDate ? new Date(productReleaseIsoDate) : null);
    setReleasedDate(productReleaseIsoDate ? dayjs(productReleaseIsoDate) : null);
  };

  return (
    <div>
      <Typography variant="body1" gutterBottom className="font-gothammedium">
        Product Dates
      </Typography>
      <Stack spacing={1} className="mt-3">
        <Stack>
          <Typography className="font-gothammedium" variant="body2">Created:</Typography>
          {loading ? (
            <Skeleton width="12rem" />
          ) : (
            <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
              {parseDateString(created)}
            </Typography>
          )}
        </Stack>
        <Stack>
          <Typography className="font-gothammedium" variant="body2">Updated:</Typography>
          {loading ? (
            <Skeleton width="12rem" />
          ) : (
            <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
              {parseDateString(lastUpdated)}
            </Typography>
          )}
        </Stack>
        <Stack>
          <Typography className="font-gothammedium" variant="body2">Release Date:</Typography>
          {loading ? (
            <Skeleton width="12rem" />
          ) : (
            <div>
              {(hasReleaseDateUpdated || updateRequestPending) ? (
                <>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      id="released-date-picker"
                      views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                      value={releasedDate}
                      onChange={(newValue) => {
                        if (newValue && dayjs(newValue).isValid()) {
                          setReleasedDate(newValue);
                          const formattedDate = newValue.format('YYYY-MM-DDTHH:mm:ss');
                          dispatch({ type: 'RELEASE_DATES', payload: formattedDate });
                        } else {
                          setReleasedDate(newValue);
                          dispatch({ type: 'RELEASE_DATES', payload: newValue?.toJSON() });
                        }
                      }}
                      format="DD/MM/YYYY HH:mm:ss"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onBlur={(event) => {
                            const rawValue = event.target.value;
                            if (rawValue === '') {
                              setReleasedDate(null);
                              setSelectedDateTimeForReleaseDate(null);
                              dispatch({ type: 'RELEASE_DATES', payload: null });
                              return;
                            }
                            const sanitizedDate = dayjs(rawValue, 'DD/MM/YYYY HH:mm:ss', true);
                            if (sanitizedDate.isValid()) {
                              setReleasedDate(sanitizedDate);
                              setSelectedDateTimeForReleaseDate(sanitizedDate);
                              const timezoneOffset = sanitizedDate.utcOffset();
                              const adjustedDate = sanitizedDate.add(timezoneOffset, 'minute');
                              dispatch({ type: 'RELEASE_DATES', payload: adjustedDate.toISOString() });
                            } else {
                              setReleasedDate(null);
                              setSelectedDateTimeForReleaseDate(null);
                              dispatch({ type: 'RELEASE_DATES', payload: null });
                              toast.error('Please enter a valid date and time.');
                            }
                          }}
                        />
                      )}
                      showDaysOutsideCurrentMonth
                      disabled={false}
                      className="w-full"
                    />
                  </LocalizationProvider>
                  <div className="flex flex-row">
                    <IconButton
                      disabled={updateRequestPending || releaseDateLoading}
                      onClick={update}
                    >
                      {!releaseDateLoading ? (
                        <CheckIcon color="success" className="text-success-600" />
                      ) : (
                        <CircularProgress size={20} thickness={5} />
                      )}
                    </IconButton>
                    <IconButton onClick={cancel}>
                      <CloseIcon
                        className="text-error-600"
                      />
                    </IconButton>
                  </div>
                </>
              ) : (
                <span className="flex flex-row items-center">
                  <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
                    {productReleaseDate}
                  </Typography>
                  <IconButton onClick={() => setHasReleaseDateUpdated(!hasReleaseDateUpdated)}>
                    <CreateIcon />
                  </IconButton>
                </span>
              )}
            </div>
          )}
        </Stack>
        <Stack>
          <Typography className="font-gothammedium" variant="body2">Last Indexed:</Typography>
          {loading ? (
            <Skeleton width="12rem" />
          ) : (
            <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
              {parseDateString(indexedDate)}
            </Typography>
          )}
        </Stack>
      </Stack>
    </div>
  );
}

export default ProductInfo;

ProductInfo.defaultProps = {
  created: '',
  lastUpdated: '',
  parentLoading: false,
  releaseDate: '',
  indexedDate: '',
};

ProductInfo.propTypes = {
  created: PropTypes.string,
  lastUpdated: PropTypes.string,
  parentLoading: PropTypes.bool,
  releaseDate: PropTypes.string,
  indexedDate: PropTypes.string,
};
